import React, { useState, useEffect, useCallback, useRef } from 'react';

const ALL_EXPOSURES = [
  { key: 'gold', label: 'Gold', applicableKey: 'useGold' },
  { key: 'bitcoin', label: 'Bitcoin', applicableKey: 'includeBitcoin' },
];

const MaxExposureSelector = ({ value = {}, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  const [inputValues, setInputValues] = useState({});
  const [questionStates, setQuestionStates] = useState({
    useGold: { value: true },
    includeBitcoin: { value: true }
  });

  // Initialize input values from props
  useEffect(() => {
    const initialInputs = {};
    Object.entries(value).forEach(([key, val]) => {
      initialInputs[key] = (val * 100).toFixed(1);
    });
    setInputValues(initialInputs);
  }, []);

  // Handle updating the actual value
  const handleUpdate = useCallback((key) => {
    const newVal = parseFloat(inputValues[key] || 0);
    // Ensure the value is between 0 and 100
    const clampedValue = Math.min(Math.max(newVal, 0), 100);

    const updatedValue = {
      ...localValue,
      [key]: clampedValue / 100,
    };
    setLocalValue(updatedValue);
    onChange(updatedValue);
  }, [localValue, inputValues, onChange]);

  // Handle input change without updating the actual value
  const handleInputChange = useCallback((key, newVal) => {
    setInputValues(prev => ({
      ...prev,
      [key]: newVal
    }));
  }, []);

  // Handle key press (Enter)
  const handleKeyPress = useCallback((event, key) => {
    if (event.key === 'Enter') {
      handleUpdate(key);
    }
  }, [handleUpdate]);

  // Determine which exposures are applicable
  const applicableExposures = ALL_EXPOSURES.filter(
    (exposure) => questionStates?.[exposure.applicableKey]?.value === true
  );

  if (applicableExposures.length === 0) {
    return (
      <div className="p-6">
        <div className="text-gray-500 text-center">No Max Exposures to Set</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {applicableExposures.map((exposure) => {
        const inputVal = inputValues[exposure.key] ?? '0.0';

        return (
          <div key={exposure.key} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {exposure.label} Maximum Exposure (%)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={inputVal}
                onChange={(e) => handleInputChange(exposure.key, e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, exposure.key)}
                className="block w-32 rounded-md border-gray-300 shadow-sm
                         focus:border-blue-500 focus:ring-blue-500
                         disabled:cursor-not-allowed disabled:bg-gray-100
                         text-right px-3 py-2"
              />
              <span className="text-gray-500">%</span>
              <button
                onClick={() => handleUpdate(exposure.key)}
                className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300
                         shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700
                         bg-white hover:bg-gray-50 focus:outline-none focus:ring-2
                         focus:ring-offset-2 focus:ring-blue-500"
              >
                Update
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MaxExposureSelector;